// src/pages/about/Gallery.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X, Folder, Search, PlayCircle, Image as ImageIcon, Video } from "lucide-react";
import { Button } from "../../components/ui/button";
import { API_URL, FALLBACK_IMAGE } from "../../lib/utils";
const heroBg = "/assets/mission-hero.png";

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

function youtubeThumb(ytId) {
  if (!ytId) return null;
  return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
}

function normalizeImageUrl(url) {
  if (!url || typeof url !== "string") return FALLBACK_IMAGE;

  const clean = url.trim();
  if (!clean) return FALLBACK_IMAGE;

  if (/^https?:\/\//i.test(clean)) return clean;

  if (clean.startsWith("/")) {
    return `${BACKEND_ORIGIN}${clean}`;
  }

  return `${BACKEND_ORIGIN}/${clean}`;
}

async function fetchGalleryFolders() {
  const res = await fetch(`${API_URL}/gallery/folders`);
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.detail || "Failed to load gallery folders");
  }

  return data;
}

async function fetchGalleryFolder(slug) {
  const res = await fetch(`${API_URL}/gallery/folders/${encodeURIComponent(slug)}`);
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.detail || "Failed to load gallery folder");
  }

  return data;
}

export default function Gallery() {
  const [folders, setFolders] = useState([]);
  const [activeSlug, setActiveSlug] = useState("");
  const [q, setQ] = useState("");

  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingFolder, setLoadingFolder] = useState(false);

  const [folderError, setFolderError] = useState("");
  const [mediaError, setMediaError] = useState("");

  const [activeFolder, setActiveFolder] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!preview) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setPreview(null);
    };

    document.addEventListener("keydown", onKeyDown);

    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = original;
    };
  }, [preview]);

  const loadFolders = async () => {
    setLoadingFolders(true);
    setFolderError("");

    try {
      const res = await fetchGalleryFolders();
      const list = Array.isArray(res?.items) ? res.items : [];

      setFolders(list);

      if (!activeSlug && list.length > 0) {
        setActiveSlug(list[0].slug);
      }

      if (!list.length) {
        setActiveFolder(null);
      }
    } catch (err) {
      setFolderError(err?.message || "Failed to load gallery folders");
      setFolders([]);
      setActiveFolder(null);
    } finally {
      setLoadingFolders(false);
    }
  };

  const loadFolder = async (slug) => {
    if (!slug) return;

    setLoadingFolder(true);
    setMediaError("");

    try {
      const res = await fetchGalleryFolder(slug);
      setActiveFolder(res || null);
    } catch (err) {
      setMediaError(err?.message || "Failed to load folder");
      setActiveFolder(null);
    } finally {
      setLoadingFolder(false);
    }
  };

  useEffect(() => {
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeSlug) {
      loadFolder(activeSlug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug]);

  const images = activeFolder?.images || [];
  const videos = activeFolder?.videos || [];

  const filteredImages = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return images;

    return images.filter((i) =>
      String(i.caption || "").toLowerCase().includes(query)
    );
  }, [images, q]);

  const filteredVideos = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return videos;

    return videos.filter((i) =>
      String(i.caption || "").toLowerCase().includes(query)
    );
  }, [videos, q]);

  const activeFolderName = activeFolder?.folder?.name || "Gallery";
  const activeFolderDescription = activeFolder?.folder?.description || "";

  return (
    <div className="min-h-screen bg-white text-stone-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt=""
            className="w-full h-full object-cover object-top"
            draggable={false}
          />
        </div>

        <div className="absolute inset-0 bg-gradient-to-br from-stone-900/85 via-stone-800/60 to-black/70" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_22%,rgba(255,255,255,.08),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(0,0,0,.35),transparent_58%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,.55),transparent_62%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-18">
          <span className="inline-flex items-center rounded-full bg-white/15 border border-white/25 backdrop-blur-sm px-4 py-2 text-xs sm:text-sm font-semibold text-white tracking-wide">
            About • Gallery
          </span>

          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mt-5 leading-[1.1]">
            Our Gallery of Activities and Events
          </h1>

          <p className="text-white/90 text-base sm:text-lg mt-4 max-w-3xl leading-relaxed">
            Explore folders from outreaches, trainings, community visits, and impact activities. Photos and YouTube videos are organized per folder.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-gradient-to-b from-white via-amber-50/30 to-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8">
            <aside className="lg:col-span-4">
              <div className="rounded-3xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                <div className="p-5 border-b border-stone-200">
                  <p className="font-heading font-bold text-stone-900 text-lg">
                    Folders
                  </p>
                  <p className="text-sm text-stone-500 mt-1">
                    Loaded directly from the gallery database.
                  </p>
                </div>

                <div className="p-4 space-y-2">
                  {loadingFolders ? (
                    <div className="space-y-3">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className="h-16 rounded-2xl bg-stone-100 animate-pulse"
                        />
                      ))}
                    </div>
                  ) : folderError ? (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      {folderError}
                    </div>
                  ) : folders.length === 0 ? (
                    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5 text-sm text-stone-600">
                      No gallery folders found. Create folders from the admin gallery manager.
                    </div>
                  ) : (
                    folders.map((f) => {
                      const isActive = f.slug === activeSlug;

                      return (
                        <button
                          key={f.id || f.slug}
                          type="button"
                          onClick={() => {
                            setQ("");
                            setActiveSlug(f.slug);
                          }}
                          className={[
                            "w-full text-left flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all",
                            isActive
                              ? "border-amber-600 bg-amber-50 shadow-sm"
                              : "border-stone-200 hover:bg-stone-50",
                          ].join(" ")}
                        >
                          <span
                            className={[
                              "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                              isActive
                                ? "bg-amber-600 text-white"
                                : "bg-stone-100 text-stone-700",
                            ].join(" ")}
                          >
                            <Folder className="w-5 h-5" />
                          </span>

                          <div className="min-w-0 flex-1">
                            <p className="font-heading font-bold text-stone-900 text-sm leading-snug truncate">
                              {f.name}
                            </p>
                            <p className="text-xs text-stone-500 mt-1">
                              {f.images_count || 0} photos • {f.videos_count || 0} videos
                            </p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8">
              <div className="rounded-3xl border border-stone-200 bg-white shadow-sm p-6 md:p-7">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="min-w-0">
                    <p className="font-heading text-xl md:text-2xl font-extrabold text-stone-900">
                      {activeFolderName}
                    </p>

                    {activeFolderDescription ? (
                      <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                        {activeFolderDescription}
                      </p>
                    ) : (
                      <p className="text-sm text-stone-600 mt-1">
                        Click any media item to preview.
                      </p>
                    )}
                  </div>

                  <div className="relative w-full sm:max-w-xs">
                    <Search className="w-4 h-4 text-stone-400 absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search captions..."
                      className="w-full rounded-full border border-stone-200 bg-white px-11 py-3 text-sm outline-none focus:ring-2 focus:ring-amber-400/30"
                    />
                  </div>
                </div>

                {mediaError ? (
                  <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {mediaError}
                  </div>
                ) : null}

                <div className="mt-7">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-heading text-lg font-extrabold text-stone-900 flex items-center gap-2">
                      <ImageIcon className="w-5 h-5 text-amber-700" />
                      Photos
                    </p>
                    <p className="text-xs text-stone-500">
                      {filteredImages.length} items
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {loadingFolder ? (
                      [...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className="aspect-square rounded-2xl bg-stone-100 animate-pulse"
                        />
                      ))
                    ) : (
                      filteredImages.map((img) => (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() =>
                            setPreview({
                              type: "image",
                              url: img.url,
                              caption: img.caption || "",
                            })
                          }
                          className="rounded-2xl overflow-hidden border border-stone-200 bg-stone-100 shadow-sm hover:shadow-lg transition-all text-left"
                          aria-label="Preview image"
                        >
                          <div className="aspect-square relative overflow-hidden">
                            <img
                              src={normalizeImageUrl(img.url)}
                              alt={img.caption || "Gallery image"}
                              className="w-full h-full object-cover block"
                              loading="lazy"
                              draggable={false}
                              onError={(e) => {
                                e.currentTarget.src = FALLBACK_IMAGE;
                              }}
                            />

                            {img.caption ? (
                              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                                <p className="text-white text-xs line-clamp-2">
                                  {img.caption}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  {!loadingFolder && filteredImages.length === 0 && (
                    <div className="mt-4 rounded-2xl border border-stone-200 bg-stone-50 p-6 text-stone-600">
                      No photos found.
                    </div>
                  )}
                </div>

                <div className="mt-10 pt-8 border-t border-stone-200">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-heading text-lg font-extrabold text-stone-900 flex items-center gap-2">
                      <Video className="w-5 h-5 text-amber-700" />
                      Videos
                    </p>
                    <p className="text-xs text-stone-500">
                      {filteredVideos.length} items
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {loadingFolder ? (
                      [...Array(2)].map((_, i) => (
                        <div
                          key={i}
                          className="aspect-video rounded-2xl bg-stone-100 animate-pulse"
                        />
                      ))
                    ) : (
                      filteredVideos.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() =>
                            setPreview({
                              type: "youtube",
                              youtube_id: v.youtube_id,
                              caption: v.caption || "",
                            })
                          }
                          className="group rounded-2xl overflow-hidden border border-stone-200 bg-stone-100 shadow-sm hover:shadow-lg transition-all text-left"
                        >
                          <div className="aspect-video relative bg-black overflow-hidden">
                            <img
                              src={youtubeThumb(v.youtube_id) || FALLBACK_IMAGE}
                              alt={v.caption || "Video"}
                              className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition block"
                              onError={(e) => {
                                e.currentTarget.src = FALLBACK_IMAGE;
                              }}
                            />

                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-14 h-14 rounded-full bg-white/15 border border-white/25 backdrop-blur flex items-center justify-center">
                                <PlayCircle className="w-7 h-7 text-white" />
                              </div>
                            </div>

                            {v.caption ? (
                              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                                <p className="text-white text-xs line-clamp-2">
                                  {v.caption}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  {!loadingFolder && filteredVideos.length === 0 && (
                    <div className="mt-4 rounded-2xl border border-stone-200 bg-stone-50 p-6 text-stone-600">
                      No videos found.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {preview && (
        <div
          className="fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Media preview"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setPreview(null)}
            aria-label="Close preview"
          />

          <div className="relative z-10 h-full w-full p-4 sm:p-6 flex items-start justify-center">
            <div className="w-full max-w-4xl rounded-3xl overflow-hidden bg-white shadow-2xl border border-stone-200 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] flex flex-col">
              <div className="flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-stone-200">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-amber-700">
                    Gallery Preview
                  </p>

                  {preview.caption ? (
                    <p className="font-heading text-base sm:text-lg font-bold text-stone-900 mt-1 truncate">
                      {preview.caption}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className="w-10 h-10 rounded-full border border-stone-200 bg-white hover:bg-stone-50 flex items-center justify-center shrink-0"
                  aria-label="Close preview"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 sm:p-5 overflow-auto">
                <div className="rounded-2xl overflow-hidden border border-stone-200 bg-stone-100">
                  {preview.type === "youtube" ? (
                    <div className="aspect-video w-full bg-black">
                      <iframe
                        className="w-full h-full"
                        src={
                          preview.youtube_id
                            ? `https://www.youtube.com/embed/${preview.youtube_id}`
                            : undefined
                        }
                        title={preview.caption || "YouTube video"}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <img
                      src={normalizeImageUrl(preview.url)}
                      alt={preview.caption || "Preview"}
                      className="w-full h-auto object-contain block"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  )}
                </div>

                {preview.caption ? (
                  <p className="text-stone-600 mt-4 leading-relaxed">
                    {preview.caption}
                  </p>
                ) : null}

                <div className="mt-5 flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full border-2"
                    onClick={() => setPreview(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}